import { useEffect, useMemo, useState } from "react";
import {
  ArrowPathIcon,
  CameraIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import usePartMutations from "../../data/usePartMutations";
import usePartsLookupMutations from "../../data/usePartsLookupMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../notifications/useNotifications";
import { IPart, IPartImageType } from "../../models/Part";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import { ebaySearchString } from "../../utils/ebaySearchString";

type SimpleField = {
  key: "oePartNumber" | "partNumber";
  label: string;
  value: string;
  setValue: (v: string) => void;
  previous: string | null;
};

type Props = {
  part: IPart;
};

const parseSuperseded = (raw: string | null | undefined): string[] =>
  (raw ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const serializeSuperseded = (list: string[]): string =>
  list
    .map((s) => s.trim())
    .filter(Boolean)
    .join(", ");

export default function StockMpnCell({ part }: Props) {
  const { patchPart } = usePartMutations();
  const { lookupMutation } = usePartsLookupMutations(part.id);
  const { saveHandlers } = useDefaultCRUDHandlers("Part Number");
  const { addNotification } = useNotification();

  const [oe, setOe] = useState(part.oePartNumber ?? "");
  const [mpn, setMpn] = useState(part.partNumber ?? "");
  const [supersededList, setSupersededList] = useState<string[]>(() => {
    const parsed = parseSuperseded(part.supersededPartNumbers);
    return parsed.length > 0 ? parsed : [""];
  });
  const [imageOpen, setImageOpen] = useState(false);

  const mpnImage = useMemo(
    () => part.images?.find((image) => image.imageType === IPartImageType.MPN),
    [part.images]
  );

  useEffect(() => setOe(part.oePartNumber ?? ""), [part.oePartNumber]);
  useEffect(() => setMpn(part.partNumber ?? ""), [part.partNumber]);
  useEffect(() => {
    const parsed = parseSuperseded(part.supersededPartNumbers);
    setSupersededList(parsed.length > 0 ? parsed : [""]);
  }, [part.supersededPartNumbers]);

  const handleOeLookup = () => {
    lookupMutation.mutate(undefined, {
      onSuccess: (data) => {
        const match = data?.data?.requestedParts?.find(
          (p) => p.partCategory?.id === part.category?.id
        );
        const found = match?.partNumber?.trim();
        if (!found) {
          addNotification({ primaryText: "No OE MPN found", variant: "info" });
          return;
        }
        if (found === (part.oePartNumber ?? "")) {
          addNotification({ primaryText: "OE MPN already up to date", variant: "info" });
          return;
        }
        patchPart.mutate(
          { id: part.id, oePartNumber: found },
          {
            ...saveHandlers,
            onSuccess: () => {
              part.oePartNumber = found;
              setOe(found);
              addNotification({ primaryText: "OE MPN updated", variant: "success" });
            },
          }
        );
      },
      onError: () => {
        addNotification({ primaryText: "OE MPN lookup failed", variant: "error" });
      },
    });
  };

  const commitSimple = (key: SimpleField["key"], next: string, previous: string | null) => {
    const trimmed = next.trim();
    const prev = previous ?? "";
    if (trimmed === prev) return;

    patchPart.mutate(
      { id: part.id, [key]: trimmed === "" ? null : trimmed },
      {
        ...saveHandlers,
        onSuccess: () => {
          if (key === "oePartNumber") part.oePartNumber = trimmed === "" ? null : trimmed;
          if (key === "partNumber") part.partNumber = trimmed === "" ? null : trimmed;
          saveHandlers.onSuccess();
        },
      }
    );
  };

  const commitSuperseded = (next: string[]) => {
    const serialized = serializeSuperseded(next);
    const previous = serializeSuperseded(parseSuperseded(part.supersededPartNumbers));
    if (serialized === previous) return;

    patchPart.mutate(
      { id: part.id, supersededPartNumbers: serialized === "" ? null : serialized },
      {
        ...saveHandlers,
        onSuccess: () => {
          part.supersededPartNumbers = serialized === "" ? null : serialized;
          saveHandlers.onSuccess();
        },
      }
    );
  };

  const updateSupersededAt = (index: number, next: string) => {
    setSupersededList((prev) => {
      const copy = [...prev];
      copy[index] = next;
      return copy;
    });
  };

  const commitSupersededAt = (index: number) => {
    const current = [...supersededList];
    current[index] = current[index].trim();
    commitSuperseded(current);
  };

  const addSuperseded = () => {
    setSupersededList((prev) => [...prev, ""]);
  };

  const removeSuperseded = (index: number) => {
    const next = supersededList.filter((_, i) => i !== index);
    const withFallback = next.length > 0 ? next : [""];
    setSupersededList(withFallback);
    commitSuperseded(next);
  };

  const simpleFields: SimpleField[] = [
    {
      key: "oePartNumber",
      label: "OE",
      value: oe,
      setValue: setOe,
      previous: part.oePartNumber,
    },
    {
      key: "partNumber",
      label: "MPN",
      value: mpn,
      setValue: setMpn,
      previous: part.partNumber,
    },
  ];

  const inputClass = classNames(
    "w-full min-w-0 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-xs text-black",
    "focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
  );

  return (
    <>
      <Modal open={imageOpen} setOpen={setImageOpen} width="max-w-3xl">
        <div className="p-2">
          {mpnImage ? (
            <img
              className="mx-auto max-h-[80vh] w-auto object-contain"
              src={mpnImage.url}
              alt="MPN"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 py-8 text-slate-500">
              <CameraIcon className="h-12 w-12" />
              <p className="text-sm">No MPN image on this part.</p>
            </div>
          )}
        </div>
      </Modal>

      <div
        className="flex flex-col gap-1"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        {simpleFields.map((field) => (
          <label
            key={field.key}
            className="flex items-center gap-1.5 text-xs text-black"
          >
            <span className="w-8 flex-none font-semibold uppercase tracking-wide text-black">
              {field.label}
            </span>
            <input
              type="text"
              value={field.value}
              onChange={(e) => field.setValue(e.target.value)}
              onBlur={(e) => commitSimple(field.key, e.target.value, field.previous)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
              }}
              className={inputClass}
            />
            {field.key === "partNumber" && (
              <>
                <button
                  type="button"
                  onClick={() => setImageOpen(true)}
                  title={mpnImage ? "View MPN image" : "No MPN image"}
                  className={classNames(
                    "flex-none rounded p-0.5 transition-colors",
                    mpnImage
                      ? "text-primary-600 hover:bg-primary-50"
                      : "text-slate-300 hover:bg-slate-100"
                  )}
                >
                  <CameraIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    field.value.trim() && openInNewTab(ebaySearchString(field.value.trim()))
                  }
                  disabled={!field.value.trim()}
                  title="Research MPN on eBay"
                  className={classNames(
                    "flex-none rounded p-0.5 transition-colors",
                    "text-primary-600 hover:bg-primary-50",
                    "disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                  )}
                >
                  <img src="/ebayLogo.svg" alt="eBay" className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    field.value.trim() &&
                    openInNewTab(
                      `https://www.google.com/search?q=${encodeURIComponent(field.value.trim())}`
                    )
                  }
                  disabled={!field.value.trim()}
                  title="Research MPN on Google"
                  className={classNames(
                    "flex-none rounded p-0.5 transition-colors",
                    "text-primary-600 hover:bg-primary-50",
                    "disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                  )}
                >
                  <MagnifyingGlassIcon className="h-4 w-4" />
                </button>
              </>
            )}
            {field.key === "oePartNumber" && (
              <>
                <button
                  type="button"
                  onClick={() =>
                    field.value.trim() && openInNewTab(ebaySearchString(field.value.trim()))
                  }
                  disabled={!field.value.trim()}
                  title="Research OE on eBay"
                  className={classNames(
                    "flex-none rounded p-0.5 transition-colors",
                    "text-primary-600 hover:bg-primary-50",
                    "disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                  )}
                >
                  <img src="/ebayLogo.svg" alt="eBay" className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleOeLookup}
                  disabled={lookupMutation.isLoading || patchPart.isLoading}
                  title="Lookup OE MPN from vehicle / catalogue"
                  className={classNames(
                    "flex-none rounded p-0.5 text-primary-600 transition-colors hover:bg-primary-50",
                    "disabled:cursor-not-allowed disabled:opacity-60"
                  )}
                >
                  {lookupMutation.isLoading ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <MagnifyingGlassIcon className="h-4 w-4" />
                  )}
                </button>
              </>
            )}
          </label>
        ))}

        {supersededList.map((entry, index) => (
          <label
            key={`sup-${index}`}
            className="flex items-center gap-1.5 text-xs text-black"
          >
            <span className="w-8 flex-none font-semibold uppercase tracking-wide text-black">
              {index === 0 ? "SUP" : ""}
            </span>
            <input
              type="text"
              value={entry}
              onChange={(e) => updateSupersededAt(index, e.target.value)}
              onBlur={() => commitSupersededAt(index)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
              }}
              className={inputClass}
            />
            {index === supersededList.length - 1 ? (
              <button
                type="button"
                onClick={addSuperseded}
                title="Add another superseded MPN"
                className="flex-none rounded p-0.5 text-primary-600 transition-colors hover:bg-primary-50"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => removeSuperseded(index)}
                title="Remove this superseded MPN"
                className="flex-none rounded p-0.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </label>
        ))}
      </div>
    </>
  );
}
